import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { useOasaraAuth } from '../hooks/useEcosystemAuthInit';

// Magic-link callbacks issue JWTs from the ecosystem (FreedomForge) project.
// MUST use the ecosystem auth client here — the per-site client would never
// recognize an ecosystem JWT and would silently drop it. Unified Auth Board 2026-05-11.
const ConfirmEmail: React.FC = () => {
  const navigate = useNavigate();
  const auth = useOasaraAuth();
  const supabase = auth.supabase;
  const [status, setStatus] = useState<'verifying' | 'success' | 'error'>('verifying');
  const [error, setError] = useState('');
  const processedRef = useRef(false);

  useEffect(() => {
    // Prevent double-processing in StrictMode
    if (processedRef.current) return;
    processedRef.current = true;

    const handleAuth = async () => {
      // Check for error in URL hash (Supabase puts errors there)
      const hash = window.location.hash.substring(1);
      const params = new URLSearchParams(hash);
      const errorParam = params.get('error');
      const errorDescription = params.get('error_description');

      if (errorParam) {
        setError(errorDescription || errorParam);
        setStatus('error');
        return;
      }

      // Get redirect destination — prefer ?next= query param (carries through
      // a fresh-tab email click where localStorage is empty), then fall back
      // to localStorage, then to /my-journey (sign-in's natural destination).
      const qp = new URLSearchParams(window.location.search);
      const redirectTo =
        qp.get('next') ||
        localStorage.getItem('oasara-auth-redirect') ||
        '/my-journey';

      // Check if already logged in (Supabase auto-detects URL token)
      try {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          console.error('[ConfirmEmail] Session error:', sessionError);
          setError(sessionError.message);
          setStatus('error');
          return;
        }

        if (session) {
          console.log('[ConfirmEmail] Session found, redirecting to:', redirectTo);
          setStatus('success');
          localStorage.removeItem('oasara-auth-redirect');
          // Clean URL hash
          window.history.replaceState(null, '', window.location.pathname);
          // Use navigate instead of window.location for smoother transition
          setTimeout(() => navigate(redirectTo, { replace: true }), 500);
          return;
        }
      } catch (err) {
        console.error('[ConfirmEmail] Error checking session:', err);
      }

      // If no session yet, wait for auth state change
      console.log('[ConfirmEmail] No session yet, waiting for auth state change...');
    };

    handleAuth();

    // Listen for auth state change (triggered when Supabase processes URL token)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('[ConfirmEmail] Auth state change:', event);
      
      if (event === 'SIGNED_IN' && session) {
        const qp = new URLSearchParams(window.location.search);
        const redirectTo =
          qp.get('next') ||
          localStorage.getItem('oasara-auth-redirect') ||
          '/my-journey';
        setStatus('success');
        localStorage.removeItem('oasara-auth-redirect');
        window.history.replaceState(null, '', window.location.pathname);
        setTimeout(() => navigate(redirectTo, { replace: true }), 500);
      }
    });

    // Timeout after 15 seconds
    const timeout = setTimeout(() => {
      setStatus(current => {
        if (current === 'verifying') {
          setError('Sign-in timed out. Please request a new magic link.');
          return 'error';
        }
        return current;
      });
    }, 15000);

    return () => {
      subscription.unsubscribe();
      clearTimeout(timeout);
    };
  }, [navigate, supabase]);

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/">
            <h1
              className="font-display text-4xl tracking-[0.3em]"
              style={{
                background: 'linear-gradient(180deg, #D4B86A 0%, #A67C00 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}
            >
              OASARA
            </h1>
          </Link>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white shadow-xl border border-sage-200 rounded-2xl p-8"
        >
          {status === 'verifying' && (
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-b from-gold-500 to-gold-700 flex items-center justify-center shadow-lg animate-pulse">
                <svg className="w-8 h-8 text-white animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </div>
              <h2 className="font-display text-2xl text-ocean-700 mb-2">Signing You In</h2>
              <p className="text-ocean-600/70">Please wait...</p>
            </div>
          )}

          {status === 'success' && (
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-b from-emerald-500 to-emerald-600 flex items-center justify-center shadow-lg">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="font-display text-2xl text-ocean-700 mb-2">You're In!</h2>
              <p className="text-ocean-600/70 mb-4">
                Welcome to OASARA. Redirecting...
              </p>
            </div>
          )}

          {status === 'error' && (
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-b from-red-500 to-red-600 flex items-center justify-center shadow-lg">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h2 className="font-display text-2xl text-ocean-700 mb-2">Verification Failed</h2>
              <p className="text-ocean-600/70 mb-6">{error}</p>
              <Link
                to="/auth"
                className="inline-block px-6 py-3 rounded-md bg-gradient-to-b from-gold-500 to-gold-700 text-white font-medium shadow-[0_4px_0_#8B6914] hover:translate-y-[-2px] transition-all"
              >
                Request New Link
              </Link>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default ConfirmEmail;
